import { computed, ref } from 'vue'

import { defineStore } from 'pinia'

import type { IChannel } from '../types'

/**
 * Channels UI Store - handles only UI state
 * NO server data, NO service imports, NO API calls
 */
export const useChannelsUIStore = defineStore('channels-ui', () => {
  // UI state only - no server data, no service imports
  const currentChannelId = ref<string | undefined>(undefined)
  const selectedChannelSlug = ref<string | undefined>(undefined)
  const searchQuery = ref('')
  const selectedType = ref('all')
  const selectedVisibility = ref('all')
  const showFilters = ref(false)
  const showCreateModal = ref(false)
  const viewMode = ref<'grid' | 'list'>('grid')
  const sortOrder = ref<'newest' | 'alphabetical' | 'unread'>('newest')
  const filterType = ref<'all' | 'unread' | 'muted'>('all')
  const selectedChannelIds = ref<string[]>([])
  const expandedCategories = ref<string[]>([])

  // Channel settings modal state
  const showChannelSettings = ref(false)
  const channelSettingsType = ref<'create' | 'edit'>('create')
  const selectedChannelForSettings = ref<string | null>(null) // Store channel ID, not full object

  // Last read timestamps per channel (snapshot when channel is opened, doesn't update when posts are marked as read)
  const channelLastReadTimestamps = ref<Record<string, Date>>({})

  // UI actions
  const setCurrentChannel = (channelId?: string) => {
    currentChannelId.value = channelId || undefined
  }

  const setSearchQuery = (query: string) => {
    searchQuery.value = query
  }

  const setSelectedType = (type: string) => {
    selectedType.value = type
  }

  const setSelectedVisibility = (visibility: string) => {
    selectedVisibility.value = visibility
  }

  const toggleFilters = () => {
    showFilters.value = !showFilters.value
  }

  const setShowFilters = (show: boolean) => {
    showFilters.value = show
  }

  const clearFilters = () => {
    searchQuery.value = ''
    selectedType.value = 'all'
    selectedVisibility.value = 'all'
  }

  const toggleCreateModal = () => {
    showCreateModal.value = !showCreateModal.value
  }

  const setShowCreateModal = (show: boolean) => {
    showCreateModal.value = show
  }

  const setViewMode = (mode: 'grid' | 'list') => {
    viewMode.value = mode
  }

  const setSortOrder = (order: typeof sortOrder.value) => {
    sortOrder.value = order
  }

  const setFilterType = (type: typeof filterType.value) => {
    filterType.value = type
  }

  const selectChannel = (channelId: string) => {
    if (!selectedChannelIds.value.includes(channelId)) {
      selectedChannelIds.value.push(channelId)
    }
  }

  const deselectChannel = (channelId: string) => {
    selectedChannelIds.value = selectedChannelIds.value.filter(id => id !== channelId)
  }

  const toggleChannelSelection = (channelId: string) => {
    if (selectedChannelIds.value.includes(channelId)) {
      deselectChannel(channelId)
    } else {
      selectChannel(channelId)
    }
  }

  const clearSelection = () => {
    selectedChannelIds.value = []
  }

  const toggleCategoryExpansion = (categoryId: string) => {
    const index = expandedCategories.value.indexOf(categoryId)
    if (index > -1) {
      expandedCategories.value.splice(index, 1)
    } else {
      expandedCategories.value.push(categoryId)
    }
  }

  // Channel settings modal actions
  const openChannelSettings = (channelId: string) => {
    selectedChannelForSettings.value = channelId
    channelSettingsType.value = 'edit'
    showChannelSettings.value = true
  }

  const openCreateChannel = () => {
    selectedChannelForSettings.value = null
    channelSettingsType.value = 'create'
    showChannelSettings.value = true
  }

  const closeChannelSettings = () => {
    showChannelSettings.value = false
    selectedChannelForSettings.value = null
  }

  // Set last read timestamp for a channel (called when channel is opened)
  const setChannelLastReadTimestamp = (channelId: string, timestamp: Date) => {
    channelLastReadTimestamps.value[channelId] = timestamp
  }

  // Get last read timestamp for a channel
  const getChannelLastReadTimestamp = (channelId: string): Date | undefined => {
    return channelLastReadTimestamps.value[channelId]
  }

  // Clear last read timestamp for a channel (when leaving channel)
  const clearChannelLastReadTimestamp = (channelId: string) => {
    delete channelLastReadTimestamps.value[channelId]
  }

  const reset = () => {
    currentChannelId.value = undefined
    selectedChannelSlug.value = undefined
    searchQuery.value = ''
    selectedType.value = 'all'
    selectedVisibility.value = 'all'
    showFilters.value = false
    showCreateModal.value = false
    viewMode.value = 'grid'
    sortOrder.value = 'newest'
    filterType.value = 'all'
    selectedChannelIds.value = []
    expandedCategories.value = []
    showChannelSettings.value = false
    channelSettingsType.value = 'create'
    selectedChannelForSettings.value = null
    channelLastReadTimestamps.value = {}
  }

  // Computed getters for filtered data (UI logic only)
  const getCurrentChannel = computed(() => (channels: IChannel[]) => {
    if (!currentChannelId.value) return null
    return channels.find(channel => channel._id === currentChannelId.value)
  })

  const getChannelBySlug = computed(() => (channels: IChannel[], slug: string) => {
    return channels.find(channel => channel.slug === slug)
  })

  const hasActiveFilters = computed(() => {
    return searchQuery.value !== '' || selectedType.value !== 'all' || selectedVisibility.value !== 'all'
  })

  return {
    // State (refs are already reactive)
    currentChannelId,
    selectedChannelSlug,
    searchQuery,
    selectedType,
    selectedVisibility,
    showFilters,
    showCreateModal,
    viewMode,
    sortOrder,
    filterType,
    selectedChannelIds,
    expandedCategories,
    showChannelSettings,
    channelSettingsType,
    selectedChannelForSettings,
    channelLastReadTimestamps,

    // Computed getters
    getCurrentChannel,
    getChannelBySlug,

    hasActiveFilters,

    // Actions
    setCurrentChannel,
    setSearchQuery,
    setSelectedType,
    setSelectedVisibility,
    toggleFilters,
    setShowFilters,
    clearFilters,
    toggleCreateModal,
    setShowCreateModal,
    setViewMode,
    setSortOrder,
    setFilterType,
    selectChannel,
    deselectChannel,
    toggleChannelSelection,
    clearSelection,
    toggleCategoryExpansion,
    openChannelSettings,
    openCreateChannel,
    closeChannelSettings,
    setChannelLastReadTimestamp,
    getChannelLastReadTimestamp,
    clearChannelLastReadTimestamp,
    reset,
  }
})
